import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { TalkJSData } from "../chat.types";

export type UseFetchTalkJSDataArgs = {
  /** Current user (to determine if logged in) */
  user: { id: number } | null;
  /** Active program whose chat_status governs the session */
  programId: number | null;
  /** Whether the query is enabled */
  isEnabled?: boolean;
};

/**
 * Fetches TalkJS configuration data from the API.
 * React Query is the source of truth.
 *
 * @example
 * ```typescript
 * const user = useStore((s) => s.user);
 * const { data: talkjsData, isLoading } = useFetchTalkJSData({ user, programId });
 *
 * if (isLoading) return <Loader />;
 *
 * return <ChatButton talkjsData={talkjsData} />;
 * ```
 */
export function useFetchTalkJSData({
  user,
  programId,
  isEnabled = true,
}: UseFetchTalkJSDataArgs) {
  return useQuery<TalkJSData | null, Error>({
    queryKey: ["talkjs", user?.id, programId],
    queryFn: async () => {
      const response = await apiRequest(
        `/chat/data/program/${programId}/`,
        "GET"
      );
      return response ?? null;
    },
    enabled: !!user && !!programId && isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Backend issues a 24h JWT. staleTime alone only authorizes a
    // refetch on focus/mount; a tab that stays focused for the full
    // session — e.g. a long-running video meeting — would otherwise
    // roll past the JWT expiry without ever hitting a refetch trigger
    // and end up holding a stale token. refetchInterval forces a
    // background refresh well before the 24h boundary regardless of
    // user interaction.
    refetchInterval: 23 * 60 * 60 * 1000, // 23 hours
  });
}

export default useFetchTalkJSData;
